import { useEffect, useState } from "react";
import { ArrowUpRight, BookOpen, Code2, PenLine, Shuffle, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { CREATOR_INFO, fetchCreatorSpotlight, safeHttpUrl, type CreatorSpotlight } from "../lib/creatorSpotlight";
import { supabasePublicAnon } from "../lib/supabase";
import { informatePathToStorageKey } from "../lib/supabaseStorageCleanup";
import { fixSupabasePublicStorageUrl } from "../lib/supabaseUrl";
import { HeroAdDismissBar, useDismissedAd } from "./HomeHeroAdCard";

const ICONS = { youtube: Youtube, blog: PenLine, vibe: Code2, book: BookOpen };
const HIDE_KEY = "fe-home-creator-spotlight-hide-until";

function plainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script,style").forEach(node => node.remove());
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

function imageUrl(pick: CreatorSpotlight): string | null {
  if (!pick.imagePath) return null;
  if (/^https?:\/\//i.test(pick.imagePath)) return safeHttpUrl(pick.imagePath);
  if (pick.category === "vibe" || !supabasePublicAnon) return null;
  const path = informatePathToStorageKey(pick.imagePath.replace(/^\/?media\//, "").replace(/^\//, ""));
  return fixSupabasePublicStorageUrl(supabasePublicAnon.storage.from("foreducator-media").getPublicUrl(path).data.publicUrl);
}

function SpotlightContent({ pick }: { pick: CreatorSpotlight }) {
  const [brokenImage, setBrokenImage] = useState(false);
  const info = CREATOR_INFO[pick.category];
  const Icon = ICONS[pick.category];
  const src = imageUrl(pick);
  return <>
    <div className="fe-creator-heading"><span><Shuffle size={13} /> 랜덤 픽</span><span>교사 크리에이터</span></div>
    <a className={`fe-creator-feature fe-creator-${pick.category}`} href={pick.href}
      target="_blank" rel="noopener noreferrer" aria-label={`${pick.title} — ${info.action} (새 탭)`}>
      <div className="fe-creator-visual">
        {src && !brokenImage ? <img src={src} alt="" decoding="async" referrerPolicy="no-referrer" onError={() => setBrokenImage(true)} /> : <Icon size={52} strokeWidth={1.3} />}
        <span className="fe-creator-category"><Icon size={13} />{info.label}</span>
      </div>
      <h2>{pick.title}</h2>
      {pick.byline ? <span className="fe-creator-byline">{plainText(pick.byline)}</span> : null}
      <p>{plainText(pick.description) || "선생님의 경험과 아이디어를 만나보세요."}</p>
      <span className="fe-creator-action">{info.action}<ArrowUpRight size={16} /></span>
    </a>
    <Link className="fe-creator-register" to={info.route}>내 콘텐츠도 알리고 싶다면 <ArrowUpRight size={13} /><small>인포메이트에 등록하면 홈에서 랜덤 소개돼요</small></Link>
  </>;
}

export function CreatorSpotlightCard() {
  const [pick, setPick] = useState<CreatorSpotlight | null>(null);
  const [loading, setLoading] = useState(true);
  const { show, dismiss } = useDismissedAd("creator-spotlight-v1", HIDE_KEY);
  useEffect(() => {
    if (!show) return;
    const controller = new AbortController();
    let mounted = true;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    void fetchCreatorSpotlight(supabasePublicAnon, controller.signal).then(result => {
      if (mounted) { setPick(result); setLoading(false); }
    }).catch(() => { if (mounted) setLoading(false); }).finally(() => window.clearTimeout(timeout));
    return () => { mounted = false; controller.abort(); window.clearTimeout(timeout); };
  }, [show]);
  if (!show) return null;
  return <article className="fe-hero-ad fe-creator-card" aria-label="교사 크리에이터 랜덤 소개" aria-busy={loading}>
    <div className="fe-creator-body">
      {pick ? <SpotlightContent key={pick.id} pick={pick} /> : <div className="fe-creator-empty">
        <Shuffle size={28} /><span>교사 크리에이터 · 랜덤 픽</span>
        <h2>{loading ? "어떤 선생님을\n만나게 될까요?" : "선생님의 창작을\n함께 나눠요"}</h2>
        <p>{loading ? "선생님의 콘텐츠를 고르고 있어요." : "유튜브·블로그·바이브코딩·책, 인포메이트에 등록된 콘텐츠를 홈에서 랜덤으로 소개해요."}</p>
        {!loading ? <Link to="/informate">인포메이트 둘러보기 <ArrowUpRight size={16} /></Link> : null}
      </div>}
    </div>
    <HeroAdDismissBar onDismiss={dismiss} />
  </article>;
}
